import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { AiFillEye, AiFillGithub } from "react-icons/ai";
import { client, urlFor } from "../../client";
import { AppWrap, MotionWrap } from "../../wrapper";

const ease = [0.22, 1, 0.36, 1];

const FILTERS = ["All", "Web App", "Mobile App"];

const Work = () => {
  const [works, setWorks] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");

  const portfolioRef = useRef(null);

  useEffect(() => {
    client.fetch('*[_type == "works"]').then((data) => setWorks(data));
  }, []);

  const filteredWorks = useMemo(() => {
    if (activeFilter === "All") return works;
    return works.filter((w) => (w.tags || []).includes(activeFilter));
  }, [works, activeFilter]);

  const visibleWorks = useMemo(() => {
    return filteredWorks.slice(0, showMore ? filteredWorks.length : 4);
  }, [filteredWorks, showMore]);

  return (
    <section className="mx-auto w-full max-w-5xl px-5 py-12 sm:px-8 sm:py-16 lg:px-12 lg:py-20">
      <div className="max-w-[64ch]">
        <p className="mb-3 text-[0.85rem] uppercase tracking-[0.12em] text-black/60">
          Selected work
        </p>
        <h2 className="text-3xl font-semibold tracking-[-0.02em] text-black/90 sm:text-4xl">
          Portfolio
        </h2>
      </div>

      {/* Filter pills */}
      <div className="mt-8 flex flex-wrap gap-2">
        {FILTERS.map((item) => {
          const isActive = activeFilter === item;
          return (
            <button
              key={item}
              type="button"
              onClick={() => {
                setActiveFilter(item);
                setShowMore(false);
              }}
              className={[
                "rounded-full px-4 py-2 text-sm transition",
                "border",
                isActive
                  ? "border-black/20 bg-black/90 text-white"
                  : "border-black/10 bg-white/60 text-black/70 hover:border-black/20 hover:text-black/90",
              ].join(" ")}
            >
              {item}
            </button>
          );
        })}
      </div>

      {/* Portfolio grid */}
      <motion.div
        ref={portfolioRef}
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease }}
        className="mt-8 grid gap-6 sm:grid-cols-2"
      >
        {visibleWorks.map((work, index) => (
          <motion.article
            key={work._id || index}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, ease, delay: index * 0.05 }}
            className="group overflow-hidden rounded-2xl border border-black/10 bg-white/60"
          >
            {/* Image + hover overlay */}
            <div className="relative aspect-[16/10] overflow-hidden bg-black/[0.03]">
              <img
                src={urlFor(work.imgUrl)}
                alt={work.name || work.title}
                loading="lazy"
                className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
              />

              <motion.div
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="absolute inset-0 flex items-center justify-center gap-3 bg-black/50 opacity-0 group-hover:opacity-100"
              >
                {work.projectLink && work.projectLink !== "none" && (
                  <a
                    href={work.projectLink}
                    target="_blank"
                    rel="noreferrer"
                    className="grid h-11 w-11 place-items-center rounded-full bg-white/90 text-black transition hover:scale-[0.98]"
                    aria-label="Open live demo"
                  >
                    <AiFillEye size={20} />
                  </a>
                )}

                {work.codeLink && work.codeLink !== "none" && (
                  <a
                    href={work.codeLink}
                    target="_blank"
                    rel="noreferrer"
                    className="grid h-11 w-11 place-items-center rounded-full bg-white/90 text-black transition hover:scale-[0.98]"
                    aria-label="Open source code"
                  >
                    <AiFillGithub size={20} />
                  </a>
                )}
              </motion.div>
            </div>

            {/* Content */}
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-4">
                <h4 className="text-base font-semibold text-black/90">
                  {work.title}
                </h4>
                <span className="whitespace-nowrap rounded-full border border-black/10 bg-white/60 px-3 py-1 text-xs text-black/60">
                  {(work.tags && work.tags[0]) || "Project"}
                </span>
              </div>

              <p className="mt-3 text-sm leading-relaxed text-black/75">
                {work.description}
              </p>
            </div>
          </motion.article>
        ))}
      </motion.div>

      {/* Show more / less */}
      {filteredWorks.length > 4 && (
        <div className="mt-8">
          <button
            type="button"
            onClick={() => {
              setShowMore((v) => {
                const next = !v;
                // when collapsing back to 3, scroll to portfolio top
                if (v && portfolioRef.current) {
                  portfolioRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
                return next;
              });
            }}
            className="border-b border-black/30 pb-1 text-sm text-black/80 transition hover:-translate-y-[1px] hover:border-black/60"
          >
            {showMore ? "Show less" : "Show more"}
          </button>
        </div>
      )}
    </section>
  );
};

export default AppWrap(
  MotionWrap(Work, "app__works"),
  "project",
  "app__primarybg"
);
